import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText,
  BarChart3,
  Calendar,
  Filter,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardAvaliacoes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pendentes: 0,
    concluidas: 0,
    emAndamento: 0,
    taxaConclusao: 0
  });
  const [avaliacoes, setAvaliacoes] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Buscar todas as avaliações
      const response = await axios.get(`${API}/api/performance/evaluations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const todasAvaliacoes = response.data || [];
      
      // Calcular estatísticas
      const concluidas = todasAvaliacoes.filter(a => a.status === 'completed').length;
      const pendentes = todasAvaliacoes.filter(a => a.status === 'pending' || !a.status).length;
      const emAndamento = todasAvaliacoes.filter(a => a.status === 'draft').length;
      const total = todasAvaliacoes.length;
      const taxaConclusao = total > 0 ? Math.round((concluidas / total) * 100) : 0;
      
      setStats({
        total,
        pendentes,
        concluidas,
        emAndamento,
        taxaConclusao
      });
      
      setAvaliacoes(todasAvaliacoes);
      
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Concluída</Badge>;
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-800">Em Andamento</Badge>;
      default:
        return <Badge className="bg-orange-100 text-orange-800">Pendente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Dashboard de Avaliações
          </h1>
          <p className="text-gray-600 mt-1">
            Acompanhamento e métricas de avaliações de desempenho
          </p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total de Avaliações
              </CardTitle>
              <FileText className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Pendentes
              </CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{stats.pendentes}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Concluídas
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.concluidas}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Taxa de Conclusão
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">{stats.taxaConclusao}%</div>
              <Progress value={stats.taxaConclusao} className="mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Ações Rápidas */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/avaliacao-wizard')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <FileText className="mr-2 h-4 w-4" />
                Nova Avaliação
              </Button>
              
              <Button
                onClick={() => navigate('/minhas-avaliacoes')}
                variant="outline"
              >
                <Clock className="mr-2 h-4 w-4" />
                Ver Pendentes
              </Button>
              
              <Button
                variant="outline"
                onClick={() => toast.info('Exportação em desenvolvimento')}
              >
                <Download className="mr-2 h-4 w-4" />
                Exportar Relatório
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Avaliações Recentes */}
        <Card>
          <CardHeader>
            <CardTitle>Avaliações Recentes</CardTitle>
            <CardDescription>
              Últimas avaliações do sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            {avaliacoes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma avaliação encontrada
              </div>
            ) : (
              <div className="space-y-4">
                {avaliacoes.slice(0, 10).map((avaliacao, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-gray-400" />
                        <div>
                          <h4 className="font-semibold">
                            Avaliação de: {avaliacao.employee_name || avaliacao.employee_id || 'Colaborador'}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Ciclo: {avaliacao.cycle_name || avaliacao.cycle_id || 'N/A'}
                            {avaliacao.evaluator_type && ` • Tipo: ${avaliacao.evaluator_type}`}
                          </p>
                          {avaliacao.status === 'completed' && avaliacao.completed_date && (
                            <p className="text-sm text-green-600 mt-1">
                              Finalizada em: {new Date(avaliacao.completed_date).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(avaliacao.status)}
                      
                      {avaliacao.status === 'completed' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/preencher-avaliacao/${avaliacao.id}`)}
                        >
                          Ver Detalhes
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => navigate(`/preencher-avaliacao/${avaliacao.id}`)}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Continuar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAvaliacoes;
